package models

import dev.gitlive.firebase.firestore.Timestamp

data class DiceRoll(
    val dieOne: Int,
    val dieTwo: Int,
)

data class RollDecision(
    val round: Round,
    val nextPlayerName: String,
)

data class BankDecision(
    val bankingPlayers: List<String>,
    val remainingPlayers: List<String>,
    val nextPlayerName: String,
)

data class PlayerRemovalDecision(
    val orderedPlayerNames: List<String>,
    val round: Round?,
    val currentPlayer: String,
    val shouldFinishGame: Boolean,
)

object GameSettings {
    const val HOST_CONTROL_LEGACY_NAME = "Host can roll or bank for other players with devices."
    const val MANUAL_ENTRY_LEGACY_NAME = "Allow Manual Entry. (Enable if using Real Dice)"

    val ManualEntry = Definition(
        id = "manual_dice_entry",
        displayName = "Manual dice entry",
        description = "Type in real dice rolls by hand instead of tapping to roll.",
        defaultValue = false,
        legacyNames = setOf(MANUAL_ENTRY_LEGACY_NAME),
    )

    val GinkoMode = Definition(
        id = "ginko_mode",
        displayName = "Ginko Mode",
        description = "Occasional round twists and mid-round Ginko events shake up tense pots.",
        defaultValue = false,
    )

    val FireworksMode = Definition(
        id = "fireworks_mode",
        displayName = "Fireworks Mode",
        description = "Limited-time: safe 7s score 76, and late doubles double the pot plus 25.",
        defaultValue = false,
    )

    val FallHarvestMode = Definition(
        id = "fall_harvest_mode",
        displayName = "Fall Harvest",
        description = "Seasonal: each of the first 3 rolls earns +5 extra. Late 7s still bust.",
        defaultValue = false,
    )

    val HalloweenMode = Definition(
        id = "halloween_mode",
        displayName = "Halloween",
        description = "Seasonal: late doubles double the pot, then add a 31-point treat. Late 7s still bust.",
        defaultValue = false,
    )

    val HostControl = Definition(
        id = "host_control_for_other_players",
        displayName = "Host plays for others",
        description = "Lets the host roll or bank for players who have their own device, for when someone steps away.",
        defaultValue = false,
        networkOnly = true,
        legacyNames = setOf(HOST_CONTROL_LEGACY_NAME),
    )

    val Nudging = Definition(
        id = "nudging_enabled",
        displayName = "Nudging",
        description = "Let players buzz whoever's taking their turn.",
        defaultValue = true,
        networkOnly = true,
    )

    val defaultSettings: List<Setting>
        get() = defaultSettingsForEpochSeconds(Timestamp.now().seconds)

    fun defaultSettingsForEpochSeconds(epochSeconds: Long): List<Setting> =
        defaultSettingsForActiveLimitedModes(LimitedTimeGameModes.activeSettingIds(epochSeconds))

    fun defaultSettingsForDate(month: Int, day: Int): List<Setting> =
        defaultSettingsForActiveLimitedModes(LimitedTimeGameModes.activeSettingIds(month = month, day = day))

    // Display + storage order, top to bottom: gameplay settings relevant to every mode first, then the
    // network-only conveniences last (which is also the group hidden on single-device games).
    private val definitions = listOf(ManualEntry, FireworksMode, FallHarvestMode, HalloweenMode, GinkoMode, HostControl, Nudging)

    fun displayName(setting: Setting): String =
        definitionFor(setting.name)?.displayName ?: setting.name

    fun description(setting: Setting): String =
        definitionFor(setting.name)?.description ?: ""

    fun isEnabled(settings: List<Setting>, definition: Definition): Boolean =
        settings.firstOrNull { it.matches(definition) }
            ?.value
            ?.toBooleanStrictOrNull()
            ?: definition.defaultValue

    // Settings that only do anything across multiple devices (host playing for others, nudging another
    // player's turn) are hidden on single-device games where they'd be no-ops.
    fun isNetworkOnlySetting(setting: Setting): Boolean = definitionFor(setting.name)?.networkOnly == true

    // Stable ordering for displaying settings, regardless of the order they were stored in.
    fun displayOrder(setting: Setting): Int =
        definitions.indexOfFirst { it.id == setting.name || setting.name in it.legacyNames }
            .let { if (it < 0) definitions.size else it }

    private fun definitionsForActiveLimitedModes(activeLimitedSettingIds: Set<String>): List<Definition> =
        definitions.filter {
            it.id !in LimitedTimeGameModes.settingIds || it.id in activeLimitedSettingIds
        }

    private fun defaultSettingsForActiveLimitedModes(activeLimitedSettingIds: Set<String>): List<Setting> =
        definitionsForActiveLimitedModes(activeLimitedSettingIds).map { definition ->
            val defaultValue =
                if (definition.id in activeLimitedSettingIds) true else definition.defaultValue
            Setting(definition.id, defaultValue.toString())
        }

    private fun definitionFor(settingName: String): Definition? =
        definitions.firstOrNull { it.id == settingName || settingName in it.legacyNames }

    private fun Setting.matches(definition: Definition): Boolean =
        name == definition.id || name in definition.legacyNames

    data class Definition(
        val id: String,
        val displayName: String,
        val defaultValue: Boolean,
        val description: String = "",
        val networkOnly: Boolean = false,
        val legacyNames: Set<String> = emptySet(),
    )
}

object LimitedTimeGameModes {
    const val FIREWORKS_SAFE_SEVEN_POINTS = 76
    const val FIREWORKS_DOUBLES_BONUS_POINTS = 25
    const val FIREWORKS_SETTING_ID = "fireworks_mode"
    const val FALL_SETTING_ID = "fall_harvest_mode"
    const val HALLOWEEN_SETTING_ID = "halloween_mode"
    const val HARVEST_SAFE_ROLL_BONUS = 5
    const val HALLOWEEN_DOUBLES_BONUS = 31

    // Add future recurring modes here (Halloween, winter holidays, tournaments, etc.) with their
    // annual window and behavior hook. The date handling below is intentionally mode-agnostic.
    private val modes = listOf(
        // First match wins: Halloween temporarily takes over fall, then Harvest returns.
        Mode(
            setting = GameSettings.HalloweenMode,
            activeWindow = AnnualDateWindow(startMonth = 10, startDay = 15, endMonth = 11, endDay = 2),
            roundModifier = RoundModifier.HALLOWEEN,
        ),
        Mode(
            setting = GameSettings.FallHarvestMode,
            activeWindow = AnnualDateWindow(startMonth = 9, startDay = 1, endMonth = 11, endDay = 30),
            roundModifier = RoundModifier.FALL_HARVEST,
        ),
        Mode(
            setting = GameSettings.FireworksMode,
            activeWindow = AnnualDateWindow(startMonth = 6, startDay = 28, endMonth = 7, endDay = 7),
            roundModifier = RoundModifier.FIREWORKS,
        ),
    )

    val settingIds: Set<String>
        get() = modes.mapTo(mutableSetOf()) { it.setting.id }

    fun activeSettingIds(epochSeconds: Long): Set<String> {
        val monthDay = utcMonthDay(epochSeconds)
        return activeSettingIds(monthDay.month, monthDay.day)
    }

    fun activeSettingIdsNow(): Set<String> = activeSettingIds(Timestamp.now().seconds)

    fun activeSettingIds(month: Int, day: Int): Set<String> =
        modes.firstOrNull { it.activeWindow.contains(month, day) }
            ?.let { setOf(it.setting.id) }
            ?: emptySet()

    /** The event running on this UTC date, for decoration outside a game (e.g. the start screen). */
    fun activeRoundModifier(epochSeconds: Long): RoundModifier? {
        val monthDay = utcMonthDay(epochSeconds)
        return activeRoundModifier(monthDay.month, monthDay.day)
    }

    fun activeRoundModifierNow(): RoundModifier? = activeRoundModifier(Timestamp.now().seconds)

    fun activeRoundModifier(month: Int, day: Int): RoundModifier? =
        modes.firstOrNull { it.activeWindow.contains(month, day) }?.roundModifier

    fun roundModifierFor(settings: List<Setting>): RoundModifier? =
        modes.firstOrNull { GameSettings.isEnabled(settings, it.setting) }?.roundModifier

    private fun utcMonthDay(epochSeconds: Long): MonthDay {
        val epochDay = epochSeconds.floorDiv(SECONDS_PER_DAY)
        val z = epochDay + DAYS_FROM_CIVIL_0000_03_01_TO_1970_01_01
        val era = z.floorDiv(DAYS_PER_ERA)
        val dayOfEra = z - era * DAYS_PER_ERA
        val yearOfEra = (dayOfEra - dayOfEra / 1460 + dayOfEra / 36524 - dayOfEra / 146096) / 365
        val dayOfYear = dayOfEra - (365 * yearOfEra + yearOfEra / 4 - yearOfEra / 100)
        val monthPrime = (5 * dayOfYear + 2) / 153
        val day = dayOfYear - (153 * monthPrime + 2) / 5 + 1
        val month = monthPrime + if (monthPrime < 10) 3 else -9
        return MonthDay(month = month.toInt(), day = day.toInt())
    }

    private fun Long.floorDiv(other: Long): Long {
        val quotient = this / other
        val remainder = this % other
        return if (remainder != 0L && ((this < 0) xor (other < 0))) quotient - 1 else quotient
    }

    private data class MonthDay(val month: Int, val day: Int)

    private data class Mode(
        val setting: GameSettings.Definition,
        val activeWindow: AnnualDateWindow,
        val roundModifier: RoundModifier,
    )

    private data class AnnualDateWindow(
        val startMonth: Int,
        val startDay: Int,
        val endMonth: Int,
        val endDay: Int,
    ) {
        fun contains(month: Int, day: Int): Boolean {
            val value = month * 100 + day
            val start = startMonth * 100 + startDay
            val end = endMonth * 100 + endDay
            return if (start <= end) {
                value in start..end
            } else {
                value >= start || value <= end
            }
        }
    }

    private const val SECONDS_PER_DAY = 86_400L
    private const val DAYS_PER_ERA = 146_097L
    private const val DAYS_FROM_CIVIL_0000_03_01_TO_1970_01_01 = 719_468L
}

object BankoGameEngine {
    fun applyRoll(
        game: DomainGame,
        diceRoll: DiceRoll,
        manuallyEnteredValue: Int? = null,
        nextRoundModifier: RoundModifier = RoundModifier.NONE,
        nextGinkoEvent: GinkoEvent = GinkoEvent.NONE,
    ): RollDecision? {
        val currentRound = game.round ?: return null
        val activePlayers = currentRound.activeOrderedPlayerNames
        val nextPlayer = nextPlayerAfter(game.currentPlayer, activePlayers) ?: return null
        val modifier = currentRound.modifier
        val ginkoEvent = currentRound.ginkoEvent

        val diceValue = manuallyEnteredValue ?: diceRoll.dieOne + diceRoll.dieTwo
        val rolledDoubles = manuallyEnteredValue == null && diceRoll.dieOne == diceRoll.dieTwo
        val manuallyEnteredDoubles = manuallyEnteredValue == 13
        val beforeFirstThreeRolls = currentRound.currentRoll <= 3

        val isFlipside = modifier == RoundModifier.FLIPSIDE
        val isFireworks = modifier == RoundModifier.FIREWORKS
        // Normally a 7 busts after the safe rolls. Flipside makes 7s safe all round (always +70)...
        val sevenBusts = diceValue == 7 && !beforeFirstThreeRolls && !isFlipside
        val dangerZoneBusts = ginkoEvent == GinkoEvent.DANGER_ZONE && diceValue == 6 && !beforeFirstThreeRolls
        val doubles = !beforeFirstThreeRolls && (rolledDoubles || manuallyEnteredDoubles)
        // ...and instead turns doubles into the bust (rather than the pot-doubler).
        val doublesBust = doubles && isFlipside

        var pointsToAdd = when {
            sevenBusts -> 0
            dangerZoneBusts -> 0
            doublesBust -> 0
            diceValue == 7 -> if (isFireworks) {
                LimitedTimeGameModes.FIREWORKS_SAFE_SEVEN_POINTS
            } else {
                70
            } // a 7 scores early; and all round under Flipside
            // Doubles re-add the pot: ×2 normally, ×3 under High Stakes.
            doubles -> when (modifier) {
                RoundModifier.HIGH_STAKES -> currentRound.currentPoints * 2
                RoundModifier.FIREWORKS ->
                    currentRound.currentPoints + LimitedTimeGameModes.FIREWORKS_DOUBLES_BONUS_POINTS
                RoundModifier.HALLOWEEN ->
                    currentRound.currentPoints + LimitedTimeGameModes.HALLOWEEN_DOUBLES_BONUS
                else -> currentRound.currentPoints
            }
            else -> diceValue
        }

        if (modifier == RoundModifier.FALL_HARVEST && beforeFirstThreeRolls && pointsToAdd > 0) {
            pointsToAdd += LimitedTimeGameModes.HARVEST_SAFE_ROLL_BONUS
        }

        // Double Trouble doubles ordinary gains — not busts, and not the pot-doubling itself.
        if (modifier == RoundModifier.DOUBLE_TROUBLE && pointsToAdd != 0 && !doubles) {
            pointsToAdd *= 2
        }
        if (ginkoEvent == GinkoEvent.HOT_STREAK && pointsToAdd != 0) {
            pointsToAdd *= 2
        }

        val isBust = pointsToAdd == 0
        val finalRoundNumber: Int
        val finalRollNumber: Int
        val finalActivePlayers: List<String>
        val finalNextPlayer: String
        val finalGinkoEvent: GinkoEvent
        val finalGinkoEventsTriggered: Int
        val finalBankLockoutTurnsRemaining: Int

        if (isBust) {
            finalRoundNumber = currentRound.roundNum + 1
            finalRollNumber = 1
            finalActivePlayers = game.orderedPlayerNames
            finalNextPlayer = nextPlayerAfter(game.currentPlayer, finalActivePlayers) ?: return null
            finalGinkoEvent = GinkoEvent.NONE
            finalGinkoEventsTriggered = 0
            finalBankLockoutTurnsRemaining = 0
        } else {
            finalRoundNumber = currentRound.roundNum
            finalRollNumber = currentRound.currentRoll + 1
            finalActivePlayers = activePlayers
            finalNextPlayer = nextPlayer
            pointsToAdd += currentRound.currentPoints
            val progression = ginkoEvent.afterRollSurvives(
                activePlayerCount = activePlayers.size,
                bankLockoutTurnsRemaining = currentRound.bankLockoutTurnsRemaining,
            )
            finalGinkoEvent = if (progression.event == GinkoEvent.NONE) nextGinkoEvent else progression.event
            finalGinkoEventsTriggered =
                currentRound.ginkoEventsTriggered + if (nextGinkoEvent != GinkoEvent.NONE) 1 else 0
            finalBankLockoutTurnsRemaining =
                if (finalGinkoEvent == progression.event) progression.bankLockoutTurnsRemaining else 0
        }

        return RollDecision(
            round = Round(
                roundNum = finalRoundNumber,
                // A bust opens a fresh round under the next modifier (Jackpot seeds the pot).
                currentPoints = if (isBust) nextRoundModifier.startingPoints() else pointsToAdd,
                dieOne = if (manuallyEnteredValue != null) 0 else diceRoll.dieOne,
                dieTwo = if (manuallyEnteredValue != null) 0 else diceRoll.dieTwo,
                currentRoll = finalRollNumber,
                activeOrderedPlayerNames = finalActivePlayers,
                modifier = if (isBust) nextRoundModifier else modifier,
                ginkoEvent = finalGinkoEvent,
                ginkoEventsTriggered = finalGinkoEventsTriggered,
                bankLockoutTurnsRemaining = finalBankLockoutTurnsRemaining,
                // The player rolling now owns these dice (currentPlayer advances to finalNextPlayer).
                rolledBy = game.currentPlayer,
            ),
            nextPlayerName = finalNextPlayer,
        )
    }

    fun bank(
        game: DomainGame,
        currentPlayer: String,
        bankingPlayerNames: List<String>,
    ): BankDecision? {
        val currentRound = game.round ?: return null
        if (currentRound.banksLocked()) return null
        val activePlayers = currentRound.activeOrderedPlayerNames
        val bankingPlayers = bankingPlayerNames
            .distinct()
            .filter { it in activePlayers }

        if (bankingPlayers.isEmpty()) {
            return null
        }

        val remainingPlayers = activePlayers.minus(bankingPlayers.toSet())
        val nextPlayerName = when {
            currentPlayer !in bankingPlayers -> currentPlayer
            remainingPlayers.isEmpty() -> game.orderedPlayerNames.firstOrNull()
            else -> nextPlayerAfter(currentPlayer, activePlayers, bankingPlayers.toSet())
        } ?: return null

        return BankDecision(
            bankingPlayers = bankingPlayers,
            remainingPlayers = remainingPlayers,
            nextPlayerName = nextPlayerName,
        )
    }

    fun Round.banksLocked(): Boolean =
        modifier.banksLockedAtRoll(currentRoll) || ginkoEvent.locksBanking

    fun removePlayer(game: DomainGame, playerName: String): PlayerRemovalDecision {
        val updatedOrderedPlayerNames = game.orderedPlayerNames.filterNot { it == playerName }
        val currentRound = game.round
        val updatedCurrentPlayer = if (game.currentPlayer == playerName) {
            updatedOrderedPlayerNames.firstOrNull().orEmpty()
        } else {
            game.currentPlayer
        }

        if (currentRound == null) {
            return PlayerRemovalDecision(
                orderedPlayerNames = updatedOrderedPlayerNames,
                round = null,
                currentPlayer = updatedCurrentPlayer,
                shouldFinishGame = false,
            )
        }

        val updatedActivePlayers = currentRound.activeOrderedPlayerNames.filterNot { it == playerName }
        val shouldFinishGame = updatedOrderedPlayerNames.size <= 1
        if (shouldFinishGame) {
            return PlayerRemovalDecision(
                orderedPlayerNames = updatedOrderedPlayerNames,
                round = currentRound.copy(activeOrderedPlayerNames = updatedActivePlayers),
                currentPlayer = updatedOrderedPlayerNames.firstOrNull().orEmpty(),
                shouldFinishGame = true,
            )
        }

        if (game.currentPlayer != playerName) {
            return PlayerRemovalDecision(
                orderedPlayerNames = updatedOrderedPlayerNames,
                round = currentRound.copy(activeOrderedPlayerNames = updatedActivePlayers),
                currentPlayer = game.currentPlayer,
                shouldFinishGame = false,
            )
        }

        if (updatedActivePlayers.isEmpty()) {
            return PlayerRemovalDecision(
                orderedPlayerNames = updatedOrderedPlayerNames,
                round = Round(
                    roundNum = currentRound.roundNum + 1,
                    currentPoints = 0,
                    dieOne = 0,
                    dieTwo = 0,
                    currentRoll = 1,
                    activeOrderedPlayerNames = updatedOrderedPlayerNames,
                ),
                currentPlayer = updatedOrderedPlayerNames.firstOrNull().orEmpty(),
                shouldFinishGame = false,
            )
        }

        // The turn passes to whoever follows the removed player in the active order, with
        // wraparound. Falls back to the first active player if the removed player wasn't in
        // the active list (an inconsistent state we still want to recover from gracefully).
        val nextActivePlayer = nextPlayerAfter(
            playerName,
            currentRound.activeOrderedPlayerNames,
            excludedPlayers = setOf(playerName),
        ) ?: updatedActivePlayers.first()
        return PlayerRemovalDecision(
            orderedPlayerNames = updatedOrderedPlayerNames,
            round = currentRound.copy(activeOrderedPlayerNames = updatedActivePlayers),
            currentPlayer = nextActivePlayer,
            shouldFinishGame = false,
        )
    }

    private fun nextPlayerAfter(
        currentPlayer: String,
        orderedPlayers: List<String>,
        excludedPlayers: Set<String> = emptySet(),
    ): String? {
        if (orderedPlayers.isEmpty()) {
            return null
        }

        var index = orderedPlayers.indexOf(currentPlayer)
        repeat(orderedPlayers.size) {
            index = (index + 1).floorMod(orderedPlayers.size)
            val nextPlayer = orderedPlayers[index]
            if (nextPlayer !in excludedPlayers) {
                return nextPlayer
            }
        }
        return null
    }

    private fun Int.floorMod(other: Int): Int = ((this % other) + other) % other
}
